import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BedbankBookingPane from './BedbankBookingPane'
import PaneBody from 'components/Common/Pane/PaneBody'
import useToggle from 'hooks/useToggle'
import OfferPageRoomGallery from '../OfferPageRoomGallery'
import BedbankPriceDetails from './BedbankPriceDetails'
import RoomList from 'components/OfferPage/Bedbank/BedbankRoomList'
import RoomFeatures from 'components/OfferPage/Bedbank/BedbankRoomFeatures'
import DetailsSection from 'components/Booking/Bedbank/DetailsSection'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { getPromotionDefaultPeriod } from 'lib/bedbank/bedbankPromotions'

import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import TextButton from 'components/Luxkit/Button/TextButton'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import useFlightPrice from 'hooks/useFlightPrice'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import WalledContent from 'components/Common/WalledContent'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import BedbankPromotion from './BedbankPromotion'
import TitleGrid from '../PackageOption/TitleGrid'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import PaneDetailsSection from 'components/Common/Pane/PaneDetailsSection'
import { ImageParams } from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import * as Analytics from 'analytics/analytics'
import { addToCartEvent } from 'analytics/optimizely/events'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import InlineGroup from 'components/Common/InlineGroup'
import SolidBoltAltIcon from 'components/Luxkit/Icons/solid/SolidBoltAltIcon'
import { useInView } from 'react-intersection-observer'
import { fireNonInteractionEvent } from 'api/googleTagManager'
import { urgencyLabelImpression } from 'analytics/eventDefinitions'

const StyledPaneDetailsSection = styled(PaneDetailsSection)`
  flex-basis: 100%;
`

const TopWrapper = styled(PaneBody)`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${rem(20)};
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.sm} {
    grid-template-columns: repeat(2, 1fr);
    padding-top: ${rem(28)};
    padding-bottom: ${rem(16)};
  }

  ${mediaQueryUp.md} {
    grid-template-columns: 25rem 1fr;
    grid-template-rows: min-content 1fr;
    grid-column-gap: ${rem(20)};
    padding-left: ${rem(28)};
    padding-right: ${rem(28)};
  }
`

const GalleryWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  grid-column: 1 / span 2;

  ${mediaQueryUp.md} {
    grid-column: auto;
    grid-row: 1 / span 2;
  }
`

const PackageHeader = styled.div`
  grid-column: 1 / span 2;
  padding-left: ${rem(20)};
  padding-right: ${rem(20)};

  ${mediaQueryUp.md} {
    grid-column: auto;
    padding-left: 0;
    padding-right: 0;
  }
`

const Col = styled.div`
  padding-left: ${rem(20)};
  padding-right: ${rem(20)};

  ${mediaQueryUp.md} {
    padding: 0;
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const SecondColumn = styled(Col)`
  grid-row: 2;

  &.pad-description {
    padding-top: ${rem(16)};
  }

  p {
    margin-top: ${rem(4)};
  }

  ${mediaQueryUp.sm} {
    grid-column: 2;
    grid-row: auto;
  }

  ${mediaQueryUp.md} {
    grid-column: auto;
  }
`

const PackageOptionsPackageRoomsWrapper = styled(PaneBody)`
  grid-column: span 3;
  overflow-anchor: none;

  > * {
    border-top: 1px solid ${({ theme }) => theme.palette.neutral.five};
  }
`

const PriceWrapper = styled.div`
  position: relative;

  ${mediaQueryUp.sm} {
    grid-row: span 2;
  }
`

const BookingButtons = styled.div`
  display: flex;

  > * + * {
    margin-left: ${rem(24)};
  }
`

const RateWrapper = styled(PaneBody)`
  display: grid;
  grid-gap: ${rem(16)};
  grid-template-columns: 1fr;
  padding: ${rem(16)} ${rem(24)};

  ${mediaQueryUp.sm} {
    grid-template-columns: 2fr 1fr;
    grid-template-rows: min-content;
    grid-gap: ${rem(16)};
  }
`

const BookButton = styled(TextButton)`
  align-self: end;
  flex-shrink: 1;

  ${mediaQueryUp.sm} {
    align-self: start;
  }

  &.hidden {
    visibility: hidden;
  }
`

const RateHeader = styled.div`
  grid-column-start: 1;
  grid-column-end: 3;
  margin-top: ${rem(8)};

  > * + * {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.sm} {
    grid-column-start: 1;
    grid-column-end: 2;
    margin-top: 0;
  }

  ${mediaQueryUp.md} {
    display: grid;
    grid-template-columns: 1fr 2fr;

    > * + * {
      margin-top: 0;
    }
  }
`

const RoomImages = styled(OfferPageRoomGallery)`
  flex-grow: 1;
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

const initialRatesToShow = 3
const imageProps: ImageParams = {
  xs: '32:13',
  sm: '35:12',
  md: '40:21',
  mdWidth: '420px',
  quality: 'good',
}

interface Props {
  checkinDate: moment.Moment;
  checkoutDate: moment.Moment;
  offer: App.BedbankOffer;
  packageView: App.BedbankOfferPackageView;
  rooms: Array<App.Occupants>;
  onBook: (pkg: App.BedbankPackage, rate: App.BedbankRate, withFlights?: boolean, flightPrice?: number) => void;
  flightsEnabled: boolean;
  fetchingRates?: boolean;
  isFilterActive: boolean;
  duration: number;
  bookedToday?: number;
}

function BedbankPackageOption(props: Props) {
  const {
    offer,
    packageView,
    rooms,
    checkinDate,
    checkoutDate,
    fetchingRates,
    flightsEnabled,
    isFilterActive,
    duration,
    bookedToday,
    onBook: onBookProp,
  } = props

  const { value: showingMoreRates, toggle: showMoreRates } = useToggle(false)
  const { value: isShowingMoreDetails, toggle: toggleShowMoreDetails } = useToggle()
  const { airport } = useContext(OfferPageStateContext)

  const rates = packageView.rates
  const promotions = packageView.promotions
  const isSoldOut = !isFilterActive && !fetchingRates && rates.length == 0 && packageView.capacityValid && checkinDate && checkoutDate

  const hasImages = packageView.package.images.length > 0

  const isInitialPackageOption = promotions[rates[0]?.id]?.id === rates[0]?.id
  let flightCheckinDate, flightCheckoutDate, nights
  if (checkinDate && checkoutDate) {
    flightCheckinDate = checkinDate
    flightCheckoutDate = checkoutDate
    nights = checkoutDate.diff(checkinDate, 'days')
  }
  if (isInitialPackageOption) {
    const period = getPromotionDefaultPeriod()
    flightCheckinDate = period.startDate
    flightCheckoutDate = period.endDate
    nights = packageView.package?.sell?.los
  }
  const [flightPrice, fetchingFlightPrice] = useFlightPrice(offer.flights?.airportCode, rooms, flightCheckinDate, flightCheckoutDate, airport?.code, nights)

  const onBook = useCallback((rate: App.BedbankRate, withFlights?: boolean) => {
    Analytics.track('add-to-cart', addToCartEvent())
    onBookProp(packageView.package, rate, withFlights, withFlights ? flightPrice : 0)
  }, [flightPrice, onBookProp, packageView.package])

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkinDate && checkinDate.diff(moment(), 'days') < minDay
  }, [checkinDate])

  const overriddenDescription = !packageView.package?.description.startsWith('\n**Size:**')

  const hasPromotions = offer.promotions?.length > 0

  const { ref: urgencyLabelsDesktopRef, inView: urgencyLabelsInView } = useInView({ triggerOnce: true })

  const bookedTodayLabel = bookedToday >= 3 && hasPromotions ? (
    <LabelGroup>
      <UrgencyLabel variant="critical1" ref={urgencyLabelsDesktopRef}>
        <InlineGroup vertical="center" spacing="xs">
          <SolidBoltAltIcon size="XS" />
          Booked {bookedToday} times today
        </InlineGroup>
      </UrgencyLabel>
    </LabelGroup>
  ) : undefined

  useEffect(() => {
    if (urgencyLabelsInView) {
      fireNonInteractionEvent(urgencyLabelImpression())
    }
  }, [urgencyLabelsInView])

  return <Pane shadow axis="vertical" data-testid="PackageOption-bedbank">
    <section>
      <TopWrapper>
        <PackageHeader>
          <TitleGrid
              title={<Heading variant="heading4">{packageView.package.name}</Heading>}
              bookmarkButton={!isSoldOut && packageView.capacityValid && (
                <TripGuard>
                  <BookmarkButton offer={offer} roomTypeId={packageView.package.roomId} />
                </TripGuard>
              )}
              urgencyLabel={bookedTodayLabel}
              bookedToday={!!bookedTodayLabel}
            />
          <SecondColumn className={overriddenDescription && 'pad-description'}>
            { isSoldOut && <UrgencyLabel variant="critical2">SOLD OUT</UrgencyLabel>}
            { !overriddenDescription && <RoomFeatures
              features={packageView.package.features}
            />}
            <MarkdownRender fontSize="medium" content={packageView.package.description} />
            <BedbankBookingPane
              isFilterActive={isFilterActive}
              rates={rates}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              offer={offer}
              packageView={packageView}
              fetchingRates={fetchingRates}
              fetchingFlightPrices={fetchingFlightPrice}
              flightPrice={flightPrice}
              flightsEnabled={flightsEnabled}
              isShowingMoreDetails={isShowingMoreDetails}
              toggleShowMoreDetails={toggleShowMoreDetails}
            />
          </SecondColumn>
        </PackageHeader>
        <GalleryWrapper>
          {!hasImages && <ImageWithReservedSpace
            withPlaceholder
            id={NO_IMAGE_AVAILABLE_ID}
            xs="16:7"
            sm="16:6"
            md="16:11"
            lg="16:10"
            alt="Sorry this image is unavailable"
          />}
          {hasImages && <RoomImages
            room={packageView.package}
            imageProps={imageProps}
            offer={offer}
          />}
        </GalleryWrapper>
      </TopWrapper>
      <StyledPaneDetailsSection isOpen={isShowingMoreDetails} onToggle={toggleShowMoreDetails}>
        <DetailsSection
          pkg={packageView.package}
        />
      </StyledPaneDetailsSection>
      <PackageOptionsPackageRoomsWrapper>
        {rates.slice(0, showingMoreRates ? rates.length : initialRatesToShow).map((rate, i) =>
          <RateWrapper
            size="lg"
            key={`${rate.id}-${promotions?.[rate.id]?.id ?? i}`}
          >
            <RateHeader>
              {promotions[rate.id] && (
                <BedbankPromotion
                  promotion={promotions[rate.id]}
                  offer={offer}
                  duration={duration}
                  rooms={rooms}
                  rate={rate}
                  direction="column"
                  showNonCancellation={!isInitialPackageOption}
                  checkInDate={checkinDate}
                  checkOutDate={checkoutDate}
                />
              )}
              {!promotions[rate.id] && (<BodyText variant="large" weight="bold">Option { i + 1 }</BodyText>)}
              <RoomList rate={rate} offer={offer} showNonCancellation promotion={promotions[rate.id]} />
            </RateHeader>
            <WalledContent enforced={offer.walledGarden && !promotions[rate.id]}>
              <Group direction="vertical" gap={16}>
                <PriceWrapper>
                  <LoadingIndicator floating opaque visible={fetchingRates || fetchingFlightPrice} />
                  <BedbankPriceDetails
                    promotions={promotions[rate.id]}
                    rate={rate}
                    rooms={rooms}
                    offer={offer}
                    flightPrice={flightPrice}
                    flightsEnabled={flightsEnabled}
                    departureTooEarly={departureTooEarly}
                    bookHandler={() => onBook(rate)}
                    bookWithFlightsHandler={() => onBook(rate, true)}
                    packageView={packageView}
                  />
                </PriceWrapper>
                {!isInitialPackageOption && (
                  <BookingButtons>
                    <BookButton kind="primary" data-testid="book-button" fit="flex" onClick={() => onBook(rate)}>
                      Book now
                    </BookButton>
                    {flightsEnabled && <BookButton
                      kind="primary"
                      data-testid="book-flights-button"
                      fit="flex"
                      onClick={() => onBook(rate, true)}
                      className={cn({ hidden: departureTooEarly })}
                    >
                      Book now
                    </BookButton>}
                  </BookingButtons>
                )}
              </Group>
            </WalledContent>
          </RateWrapper>,
        )}
        {rates.length > initialRatesToShow &&
          <ShowMoreContent variant="medium" align="center">
            <SmallToggleButton
                on={showingMoreRates}
                onClick={showMoreRates}
              >
              {showingMoreRates ? 'See less options' : 'Show all options'}
            </SmallToggleButton>
          </ShowMoreContent>
          }
      </PackageOptionsPackageRoomsWrapper>
    </section>
  </Pane>
}

export default BedbankPackageOption
